<template>
  <div class="main audit-log-page">
    <h3 class="title">Analytics Dashboard</h3>

    <div class="audit-log-page__toolbar">
      <div class="audit-log-page__filters">
        <el-radio-group v-model="days" size="small">
          <el-radio-button v-for="r in DAY_RANGES" :key="r.value" :label="r.value">{{ r.label }}</el-radio-button>
        </el-radio-group>

        <span class="audit-log-page__filter-divider"></span>

        <div class="audit-log-page__filter-group">
          <label class="audit-log-page__filter-label">Region</label>
          <el-select v-model="region" size="small" class="audit-log-page__region-filter">
            <el-option label="All Regions" value="All"></el-option>
            <el-option v-for="r in REGION_OPTIONS" :key="r" :label="r" :value="r"></el-option>
          </el-select>
        </div>

        <div class="audit-log-page__filter-group">
          <label class="audit-log-page__filter-label">Consultant</label>
          <el-select
            v-model="consultants"
            multiple
            filterable
            remote
            reserve-keyword
            collapse-tags
            placeholder="Search consultant"
            size="small"
            class="audit-log-page__consultant-filter"
            :loading="consultantSearchLoading"
            :remote-method="searchConsultants"
          >
            <el-option v-for="name in consultantOptions" :key="name" :label="name" :value="name"></el-option>
          </el-select>
        </div>
      </div>

      <div class="audit-log-page__selected-consultants" v-if="consultants.length">
        <el-tag
          v-for="name in consultants"
          :key="name"
          size="small"
          closable
          @close="removeConsultant(name)"
        >{{ name }}</el-tag>
      </div>

      <span class="audit-log-page__since" v-if="summary && summary.earliestRecord">
        Data recorded since {{ formatDate(summary.earliestRecord) }}
        <el-tooltip effect="dark" placement="bottom" content="Entries with an unspecified source or a &quot;Walton Portal&quot; source are excluded from these analytics.">
          <em class="el-icon-info"></em>
        </el-tooltip>
      </span>
    </div>

    <el-tabs v-model="section">
      <el-tab-pane label="Overview" name="overview">
        <section class="kpi-row">
          <el-row :gutter="16" type="flex">
            <el-col :span="4">
              <MetricTile label="Active users" :value="summary ? formatCount(summary.distinctUsers) : '—'" :caption="'Last ' + days + ' days'"></MetricTile>
            </el-col>
            <el-col :span="4">
              <MetricTile
                label="Response time"
                :value="formatMsOrDash(summary && summary.avgLatencyMs) + ' – ' + formatMsOrDash(summary && summary.p95LatencyMs)"
                caption="Avg - P95 · Web requests"
              ></MetricTile>
            </el-col>
            <el-col :span="4">
              <MetricTile :caption="'Last ' + days + ' days'" :value="chartsLoading ? '—' : formatCount(summary && summary.loginAttempts)">
                <template slot="label">
                  Login attempts
                  <el-tooltip effect="dark" placement="top">
                    <div slot="content">
                      Counts requests to the Azure AD B2C login page redirect, fired each time a user reaches<br />
                      the login screen - summed across web, Android, and iOS.
                    </div>
                    <em class="el-icon-info"></em>
                  </el-tooltip>
                </template>
              </MetricTile>
            </el-col>
            <el-col :span="4">
              <MetricTile :caption="'Last ' + days + ' days'" :value="chartsLoading ? '—' : formatCount(summary && summary.successfulLogins)">
                <template slot="label">
                  Successful logins
                  <el-tooltip effect="dark" placement="top">
                    <div slot="content">
                      Counts requests to the Azure AD B2C redirect fired after a login completes<br />
                      successfully - summed across web, Android, and iOS.
                    </div>
                    <em class="el-icon-info"></em>
                  </el-tooltip>
                </template>
              </MetricTile>
            </el-col>
            <el-col :span="4">
              <MetricTile label="Most used platform" :value="chartsLoading ? '—' : topPlatform.label" :caption="topPlatform.caption"></MetricTile>
            </el-col>
            <el-col :span="4">
              <MetricTile label="Busiest day" :value="chartsLoading ? '—' : topDay.label" :caption="topDay.caption"></MetricTile>
            </el-col>
          </el-row>
        </section>

        <el-row :gutter="16" type="flex" class="chart-row">
          <el-col :span="12">
            <ChartCard
              title="Requests by source"
              :subtitle="'Website vs. mobile app usage · ' + formatCount(bySourceTotal) + ' total'"
              :tooltip="CHART_TOOLTIPS.bySource"
            >
              <PieChart v-if="!chartsLoading" :data="bySource"></PieChart>
            </ChartCard>
          </el-col>
          <el-col :span="12">
            <ChartCard
              title="Peak usage"
              :subtitle="'Requests by hour of day and day of week, last ' + days + ' days'"
              :tooltip="CHART_TOOLTIPS.peakUsage"
            >
              <HeatmapChart v-if="!chartsLoading" :data="hourlyHeatmap"></HeatmapChart>
            </ChartCard>
          </el-col>
        </el-row>

        <el-row :gutter="16" type="flex" class="chart-row">
          <el-col :span="12">
            <ChartCard
              title="Request volume"
              :subtitle="'Audit log entries per day, last ' + days + ' days'"
              :tooltip="CHART_TOOLTIPS.requestVolume"
            >
              <DailyTrendChart
                v-if="!chartsLoading"
                :data="dailyVolume"
                date-key="date"
                value-key="count"
                name="Requests"
                color="#5b8ff9"
                :value-formatter="formatCompactNumber"
                :date-formatter="formatAxisDate"
              ></DailyTrendChart>
            </ChartCard>
          </el-col>
          <el-col :span="12">
            <ChartCard title="Average response time" subtitle="Web requests only, per day" :tooltip="CHART_TOOLTIPS.responseTime">
              <DailyTrendChart
                v-if="!chartsLoading"
                :data="dailyLatency"
                date-key="date"
                value-key="avgLatencyMs"
                name="Avg latency"
                color="#5ad8a6"
                :value-formatter="formatMs"
                :date-formatter="formatAxisDate"
              ></DailyTrendChart>
            </ChartCard>
          </el-col>
        </el-row>

        <ChartCard title="Slow requests" subtitle="% of web requests over 2s, per day" :tooltip="CHART_TOOLTIPS.slowRequests" wide>
          <DailyTrendChart
            v-if="!chartsLoading"
            :data="slowRate"
            date-key="date"
            value-key="slowRate"
            name="Over 2s"
            color="#f2637b"
            :value-formatter="formatPercent"
            :date-formatter="formatAxisDate"
          ></DailyTrendChart>
        </ChartCard>
      </el-tab-pane>

      <el-tab-pane label="Pages" name="pages">
        <el-row :gutter="16" type="flex" class="chart-row">
          <el-col :span="24">
            <el-card shadow="never" class="chart-card">
              <div class="chart-card__header">
                <div>
                  <h4 class="chart-card__title">
                    Page visits
                    <el-tooltip effect="dark" placement="top">
                      <div slot="content">
                        A visit counts a user hitting any endpoint for a page once per day - not summed<br />
                        per endpoint call.<br /><br />
                        Includes: AOS, WEB, iOS. CMS admin activity is excluded.
                      </div>
                      <em class="el-icon-info"></em>
                    </el-tooltip>
                  </h4>
                  <p class="chart-card__subtitle">Visits and distinct visitors per page, last {{ days }} days</p>
                </div>
                <el-radio-group v-model="pageVisitsView" size="mini">
                  <el-radio-button label="list">List</el-radio-button>
                  <el-radio-button label="table">Table</el-radio-button>
                </el-radio-group>
              </div>
              <div class="chart-card__body">
                <NamedCountList v-if="pageVisitsView === 'list'" :rows="pageVisitsRows"></NamedCountList>
                <el-table v-else :data="pageEngagement" size="small" style="width: 100%" v-loading="chartsLoading">
                  <el-table-column label="Page" prop="name" show-overflow-tooltip></el-table-column>
                  <el-table-column label="Total visits">
                    <template slot-scope="scope">{{ formatCount(scope.row.totalVisits) }}</template>
                  </el-table-column>
                  <el-table-column label="Distinct visitors">
                    <template slot-scope="scope">{{ formatCount(scope.row.distinctVisitors) }}</template>
                  </el-table-column>
                </el-table>
              </div>
            </el-card>
          </el-col>
        </el-row>

        <ChartCard
          title="Page trend"
          :subtitle="'Visits per day for the top ' + pageDailyTrend.length + ' pages, last ' + days + ' days'"
          :tooltip="CHART_TOOLTIPS.pageTrend"
          wide
        >
          <PageTrendChart
            v-if="!chartsLoading"
            :data="pageDailyTrend"
            :value-formatter="formatCompactNumber"
            :date-formatter="formatAxisDate"
          ></PageTrendChart>
        </ChartCard>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import { auditLogDashboard, auditLogConsultants } from "@/request/api";
import MetricTile from "./MetricTile.vue";
import ChartCard from "./ChartCard.vue";
import DailyTrendChart from "./DailyTrendChart.vue";
import HeatmapChart from "./HeatmapChart.vue";
import PageTrendChart from "./PageTrendChart.vue";
import PieChart from "./PieChart.vue";

const NamedCountList = {
  name: "NamedCountList",
  props: { rows: { type: Array, default: () => [] } },
  computed: {
    max() {
      return this.rows.reduce((m, r) => Math.max(m, r.count || 0), 0);
    }
  },
  methods: {
    barWidth(count) {
      if (!this.max) return "0%";
      return Math.max(0, Math.min(100, ((count || 0) / this.max) * 100)) + "%";
    }
  },
  render(h) {
    if (!this.rows.length) {
      return h("div", { staticClass: "named-count-list__empty" }, "No data for these filters.");
    }
    const rows = this.rows.map(r =>
      h("div", { staticClass: "named-count-list__row", key: r.name }, [
        h("span", { staticClass: "named-count-list__name", attrs: { title: r.name } }, r.name),
        h("span", { staticClass: "named-count-list__bar-track" }, [
          h("span", { staticClass: "named-count-list__bar", style: { width: this.barWidth(r.count) } })
        ]),
        h("span", { staticClass: "named-count-list__count" }, (r.count || 0).toLocaleString())
      ])
    );
    return h("div", { staticClass: "named-count-list" }, rows);
  }
};

export default {
  name: "AuditLog",
  components: {
    MetricTile,
    ChartCard,
    DailyTrendChart,
    HeatmapChart,
    PageTrendChart,
    PieChart,
    NamedCountList
  },
  data() {
    return {
      DAY_RANGES: [
        { label: "7 days", value: 7 },
        { label: "30 days", value: 30 },
        { label: "90 days", value: 90 }
      ],
      section: "overview",
      pageVisitsView: "list",
      days: 30,

      // Fixed labels for the WigRegion/location CASE mapping on WaltonAXCustomersTable - see
      // REGION_CASE_EXPR in CmsAuditLogAnalyticsDaoImpl. Not fetched from the server since the
      // mapping itself is fixed, not data-driven.
      REGION_OPTIONS: ["Japan", "Dubai", "Taiwan", "Singapore", "Hong Kong"],

      // Every chart on this dashboard is scoped server-side to app traffic only
      // (scopeParams() always sends cms: false, app: true) - CMS admin-panel activity never
      // shows up here. dailyLatency/slowRate additionally filter to Source = 'WEB' in
      // CmsAuditLogAnalyticsDaoImpl, since only web requests carry the request/response
      // timestamps those queries need - Android/iOS requests don't have them.
      CHART_TOOLTIPS: {
        bySource: "Total app requests split by platform, for the selected date range and filters. Includes: AOS, WEB, iOS. CMS admin activity is excluded.",
        peakUsage: "Request volume grouped by hour of day and day of week, for the selected date range and filters. Includes: AOS, WEB, iOS. CMS admin activity is excluded.",
        requestVolume: "Total audit log entries recorded per day. Includes: AOS, WEB, iOS. CMS admin activity is excluded.",
        responseTime: "Average and P95 response latency per day. Includes: WEB only - Android/iOS requests don't carry the request/response timestamps this needs.",
        slowRequests: "Percentage of requests taking longer than 2 seconds, per day. Includes: WEB only - Android/iOS requests don't carry the request/response timestamps this needs.",
        pageTrend: "Daily visit counts for the top pages by total visits. Includes: AOS, WEB, iOS. CMS admin activity is excluded."
      },
      region: "All",
      consultants: [],
      consultantOptions: [],
      consultantSearchLoading: false,

      summary: null,
      dailyVolume: [],
      dailyLatency: [],
      bySource: [],
      hourlyHeatmap: [],
      slowRate: [],
      pageEngagement: [],
      pageDailyTrend: [],

      chartsLoading: true
    };
  },
  computed: {
    bySourceTotal() {
      return this.bySource.reduce((sum, s) => sum + (s.count || 0), 0);
    },
    topPlatform() {
      const MOBILE_SOURCES = ["AOS", "IOS"];
      let web = 0;
      let mobile = 0;
      this.bySource.forEach(s => {
        const name = (s.name || "").toUpperCase();
        if (MOBILE_SOURCES.indexOf(name) !== -1) {
          mobile += s.count || 0;
        } else if (name === "WEB") {
          web += s.count || 0;
        }
      });
      const total = web + mobile;
      if (!total) return { label: "—", caption: "" };
      const isWeb = web >= mobile;
      const pct = Math.round(((isWeb ? web : mobile) / total) * 100);
      return { label: isWeb ? "Web" : "Mobile", caption: pct + "% of requests" };
    },
    topDay() {
      const totalsByDay = {};
      this.hourlyHeatmap.forEach(h => {
        totalsByDay[h.dayOfWeek] = (totalsByDay[h.dayOfWeek] || 0) + (h.count || 0);
      });
      let bestDay = null;
      let bestCount = -1;
      Object.keys(totalsByDay).forEach(day => {
        if (totalsByDay[day] > bestCount) {
          bestCount = totalsByDay[day];
          bestDay = day;
        }
      });
      if (!bestDay) return { label: "—", caption: "" };
      return { label: bestDay, caption: this.formatCount(bestCount) + " requests" };
    },
    pageVisitsRows() {
      return this.pageEngagement.map(p => ({ name: p.name, count: p.totalVisits }));
    }
  },
  watch: {
    days() {
      this.fetchCharts();
    },
    region() {
      this.fetchCharts();
    },
    consultants() {
      this.fetchCharts();
    }
  },
  methods: {
    scopeParams() {
      return {
        days: this.days,
        cms: false,
        app: true,
        region: this.region === "All" ? undefined : this.region,
        consultants: this.consultants.length ? this.consultants : undefined
      };
    },
    removeConsultant(name) {
      this.consultants = this.consultants.filter(c => c !== name);
    },
    searchConsultants(query) {
      if (!query) {
        this.consultantOptions = [];
        return;
      }
      this.consultantSearchLoading = true;
      auditLogConsultants({ search: query })
        .then(res => {
          this.consultantOptions = res.data.payload || [];
        })
        .finally(() => {
          this.consultantSearchLoading = false;
        });
    },
    fetchCharts() {
      this.chartsLoading = true;
      const params = this.scopeParams();
      auditLogDashboard(params)
        .then(res => {
          const dashboard = res.data.payload || {};
          this.summary = dashboard.summary;
          this.dailyVolume = dashboard.dailyVolume || [];
          this.dailyLatency = dashboard.dailyLatency || [];
          this.bySource = dashboard.bySource || [];
          this.hourlyHeatmap = dashboard.hourlyHeatmap || [];
          this.slowRate = dashboard.slowRate || [];
          this.pageEngagement = dashboard.pageEngagement || [];
          this.pageDailyTrend = dashboard.pageDailyTrend || [];
          this.chartsLoading = false;
        })
        .catch(() => {
          this.$message.error("Failed to load audit log analytics");
          this.chartsLoading = false;
        });
    },
    formatCount(v) {
      if (v === null || v === undefined) return "—";
      return v.toLocaleString();
    },
    formatCompactNumber(v) {
      if (v === null || v === undefined) return "—";
      try {
        return new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 }).format(v);
      } catch (e) {
        return String(v);
      }
    },
    formatMs(v) {
      if (v === null || v === undefined) return "—";
      return v >= 1000 ? (v / 1000).toFixed(1) + "s" : Math.round(v) + "ms";
    },
    formatMsOrDash(v) {
      if (v === null || v === undefined) return "—";
      return this.formatMs(v);
    },
    formatPercent(v) {
      if (v === null || v === undefined) return "—";
      return v.toFixed(1) + "%";
    },
    formatAxisDate(v) {
      if (!v) return "";
      return new Date(v).toLocaleDateString(undefined, { month: "short", day: "numeric" });
    },
    formatDate(v) {
      if (!v) return "—";
      return new Date(v).toLocaleDateString();
    }
  },
  created: function() {
    this.$emit("header", true);
    this.fetchCharts();
  }
};
</script>

<style scoped>
.audit-log-page__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.audit-log-page__filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  row-gap: 10px;
}
.audit-log-page__filter-divider {
  width: 1px;
  height: 22px;
  background: #dcdfe6;
}
.audit-log-page__filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}
.audit-log-page__filter-label {
  font-size: 12px;
  color: #909399;
  white-space: nowrap;
}
.audit-log-page__region-filter {
  width: 180px;
}
.audit-log-page__consultant-filter {
  width: 280px;
}
.audit-log-page__selected-consultants {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin: 0 16px;
  min-width: 0;
}
.audit-log-page__since {
  font-size: 12px;
  color: #909399;
  white-space: nowrap;
}
.kpi-row {
  margin-bottom: 20px;
}
.chart-card {
  margin-bottom: 20px;
}
.chart-row {
  margin-bottom: 24px;
}
.chart-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}
.chart-card__title {
  margin: 0;
  font-size: 15px;
  color: #003448;
  display: flex;
  align-items: center;
  gap: 6px;
}
.chart-card__subtitle {
  margin: 4px 0 0;
  font-size: 12px;
  color: #909399;
}
.chart-card__body {
  margin-top: 14px;
}
</style>
<style>
.chart-card {
  height: 100%;
  box-sizing: border-box;
}
.chart-card .el-card__body {
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}
.chart-card .el-card__body .chart-card__body {
  flex: 1;
}
.named-count-list__row {
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #ebeef5;
  gap: 12px;
}
.named-count-list__row:last-child {
  border-bottom: none;
}
.named-count-list__name {
  width: 220px;
  flex-shrink: 0;
  font-size: 13px;
  color: #606266;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.named-count-list__bar-track {
  flex: 1;
  height: 8px;
  background: #f2f4f7;
  border-radius: 4px;
  overflow: hidden;
}
.named-count-list__bar {
  display: block;
  height: 100%;
  background: #5b8ff9;
  border-radius: 4px;
}
.named-count-list__count {
  width: 60px;
  flex-shrink: 0;
  text-align: right;
  font-size: 13px;
  color: #606266;
}
.named-count-list__empty {
  padding: 12px 0;
  font-size: 13px;
  color: #909399;
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/components/auditLog/AuditLog.vue